import { Info, Search, Pin } from 'lucide-react';
import { type GroupChannelProps } from '@sendbird/uikit-react/GroupChannel';
import {
  GroupChannelHeader,
  type GroupChannelHeaderProps,
} from '@sendbird/uikit-react/GroupChannel/components/GroupChannelHeader';
import { useMemo } from 'react';
import { useTranslate } from 'ra-core';
import { PinnedMessagesPopover } from './PinnedMessagesPopover/PinnedMessagesPopover';
import { useGroupChannelContext } from '@sendbird/uikit-react/GroupChannel/context';
import { getChatChannelName } from '../../../utils/getChatChannelName';
import { Loading } from '../../../components/admin/loading';
import { Button } from '../../../components/ui/button';
import { useCurrentUser } from '../../../auth/currentUserContext';

type GroupCustomHeaderProps = Pick<
  GroupChannelProps,
  'onSearchClick' | 'onChatHeaderActionClick'
> &
  GroupChannelHeaderProps;

export const GroupCustomHeader = (props: GroupCustomHeaderProps) => {
  const translate = useTranslate();
  const currentUser = useCurrentUser();
  const { currentChannel } = useGroupChannelContext();

  const chatName = useMemo(
    () => getChatChannelName(currentChannel, currentUser),
    [currentChannel, currentUser]
  );

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- types are a lie for currentChannel
  if (!currentChannel) {
    return <Loading />;
  }

  return (
    <GroupChannelHeader
      {...props}
      renderMiddle={() => (
        <div className="ml-2 w-[45vw] self-start truncate text-center text-lg font-medium">
          {chatName}
        </div>
      )}
      renderRight={() => (
        <div className="flex items-center gap-1">
          {!currentChannel.isSuper && (
            <PinnedMessagesPopover
              triggerLabel={translate('chat.pinnedMessages')}
              trigger={<Pin />}
            />
          )}

          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={translate('chat.searchMessages')}
            onClick={props.onSearchClick}
          >
            <Search />
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={translate('chat.channelSettings')}
            onClick={props.onChatHeaderActionClick}
          >
            <Info />
          </Button>
        </div>
      )}
    />
  );
};
